import { useState, useMemo } from 'react'
import logger from '../utils/logger'
import { authenticatedFetch } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'

/**
 * Custom hook to handle campaign data fetching and management
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.showHistoricalData - Whether to show historical (5 years) data or month-to-date
 * @param {boolean} options.showInactiveCampaigns - Whether to include inactive campaigns
 * @param {Date} options.startDate - Start date for filtering
 * @param {Date} options.endDate - End date for filtering
 * @returns {Object} Campaign data and methods
 */
const useCampaignData = ({
  showHistoricalData = false,
  showInactiveCampaigns = false,
  startDate = new Date(),
  endDate = new Date()
}) => {
  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fiveYearsAgo = useMemo(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - 5)
    return date
  }, [])

  /**
   * Fetch campaigns with current filters
   */
  const fetchCampaigns = async () => {
    try {
      setLoading(true)
      setError(null)

      const effectiveStartDate = showHistoricalData ? fiveYearsAgo : startDate

      const startDateISO = effectiveStartDate.toISOString()
      const endDateISO = endDate.toISOString()

      const hideInactive = !!(!showHistoricalData && !showInactiveCampaigns)

      logger.debug('Fetching campaigns with filter settings:', {
        startDate: startDateISO.substring(0, 10),
        endDate: endDateISO.substring(0, 10),
        showHistoricalData,
        showInactiveCampaigns,
        hideInactive
      })

      const response = await authenticatedFetch(
        `${apiEndpoints.campaigns}?startDate=${startDateISO}&endDate=${endDateISO}&hideInactive=${hideInactive}`
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch campaigns: ${response.status}`)
      }

      const data = await response.json()

      const dateRangeDescription = showHistoricalData
        ? 'historical data (5 years)'
        : `month-to-date (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`

      logger.debug(
        `Campaigns data received: ${data.length} campaigns retrieved for ${dateRangeDescription}`
      )

      if (data.length === 0) {
        logger.warn(`No campaigns received from API for ${dateRangeDescription}`)
      }

      setCampaigns(data)
    } catch (err) {
      logger.error('Error fetching campaigns:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Initialize data — fetches campaigns from local DB.
   * Sync is handled by Heroku Scheduler; no client-side refresh.
   */
  const initializeData = async () => {
    await fetchCampaigns()
  }

  /**
   * Get chip color based on plan remaining percentage
   * @param {number} percentage - Plan remaining percentage
   * @returns {string} MUI color name
   */
  const getPlanRemainingColor = (percentage) => {
    if (percentage > 50) return 'success'
    if (percentage > 25) return 'warning'
    return 'error'
  }

  return {
    campaigns,
    loading,
    error,
    fiveYearsAgo,

    fetchCampaigns,
    initializeData,
    getPlanRemainingColor
  }
}

export default useCampaignData
